// リレー転送 UI（M4）。
//   送信: .fmapply を暗号化 → リレーへ保存 → 6桁コード + 手入力文字列を表示
//         （Unity が受信待ちの箱を用意している場合はその箱へ直接投函する。
//          または送信側でカメラを使い、Unity の受信待ち QR を直接読み取って投函する）
//   受信: 6桁コード + 鍵の文字列（または QR/URL の貼り付け）で .fmavatar を取得して読み込む
//
// 送信結果に QR は出さない（Unity 側に QR を読む手段が無く、Web 自身を開く URL に
// しかならないため）。コード文字列の手入力、またはカメラでの直接読み取りのみ。
// XSS 防止のため innerHTML は使わない。

import { buildClipPack, clipPackFileName } from "../core/clipPack.ts";
import { describeError } from "../core/errors.ts";
import { fmApplyFileName, serializeFmApply, type ApplyOptions } from "../core/fmapply.ts";
import { store } from "../store/state.ts";
import {
  decryptFromTransfer,
  encryptForTransfer,
  encryptWithKey,
  formatSecret,
  parseClipPackRecvSecret,
  parseScannedQr,
  parseTransferSecret,
  type TransferSecret,
} from "../transfer/crypto.ts";
import { QrScanner } from "../transfer/qrScan.ts";
import {
  checkUploadStatus,
  downloadFromRelay,
  getRelayBaseUrl,
  RelayError,
  revokeUpload,
  setRelayBaseUrl,
  uploadToRelay,
  fillMailbox,
} from "../transfer/relayClient.ts";
import {
  addSendHistoryEntry,
  applyUploadStatus,
  listSendHistoryEntries,
  removeSendHistoryEntry,
  type SendHistoryEntry,
} from "../transfer/sendHistory.ts";
import { el, clear, modal } from "./dom.ts";

/**
 * 投函に使った（使おうとした）受信待ちの鍵・IV。
 * 受信待ちの鍵・IV は Unity 側が決めた固定値なので、同じ箱へ内容を変えて再暗号化すると
 * AES-GCM の IV 再利用になる。成功・失敗にかかわらず1回使ったら二度と使わない。
 */
const usedMailboxTargets = new Set<string>();

/** 投函先を「使用済み」にする。既に使用済みなら false。 */
function consumeMailboxTarget(t: TransferSecret): boolean {
  const key = `${t.code}:${t.keyB64}:${t.ivB64}`;
  if (usedMailboxTargets.has(key)) return false;
  usedMailboxTargets.add(key);
  return true;
}

const USED_TARGET_MESSAGE =
  "この受信待ちは既に使用済みです（安全のため同じ受信待ちへは1回しか送れません）。Unity 側で受信待ちをやり直してください。";

/** Unity から `#send:...` で指定された投函先。null なら通常の送信モード。 */
let pendingSendTarget: TransferSecret | null = null;

export function setPendingSendTarget(t: TransferSecret | null): void {
  pendingSendTarget = t;
}

export function getPendingSendTarget(): TransferSecret | null {
  return pendingSendTarget;
}

/**
 * ダウンロード進捗バー。pct が数値ならその割合を表示し、undefined（復号中・読み込み中など
 * 進捗が数値化できない段階）なら不確定表示（indeterminate）にする。
 */
function transferProgress(): { element: HTMLElement; show: () => void; update: (pct?: number) => void; hide: () => void } {
  const bar = el("progress", { class: "transfer-progress", max: "100" }) as HTMLProgressElement;
  bar.hidden = true;
  return {
    element: bar,
    show: () => {
      bar.hidden = false;
      bar.removeAttribute("value");
    },
    update: (pct?: number) => {
      if (pct === undefined) bar.removeAttribute("value");
      else bar.value = pct;
    },
    hide: () => {
      bar.hidden = true;
    },
  };
}

/** Unity から `#sendclip:...` で指定された投函先。null なら通常の送信モード。 */
let pendingClipPackSendTarget: TransferSecret | null = null;

export function setPendingClipPackSendTarget(t: TransferSecret | null): void {
  pendingClipPackSendTarget = t;
}

export function getPendingClipPackSendTarget(): TransferSecret | null {
  return pendingClipPackSendTarget;
}

const KIND_LABEL: Record<"fmavatar" | "clippack", string> = {
  fmavatar: "アバター",
  clippack: "クリップパック",
};

/**
 * 「カメラで読み取る」ボタン + スキャンプレビュー領域を作る。
 * mode "mailbox"（既定）: Unity の受信待ち QR（`send:` / `sendclip:` プレフィックス）を検出すると、
 *   種別が一致する場合のみ onScanned を呼ぶ（Web→Unity 送信で使用）。
 * mode "download": Unity の送信結果画面が表示する QR（`code:鍵:IV` 形式・`recvclip:` 形式）を受け付ける
 *   （Unity→Web 受信で使用）。`send:` / `sendclip:`（受信待ち QR）を読んだ場合はエラー案内を出す。
 * 戻り値の stop() はダイアログを閉じる際に必ず呼ぶこと（カメラの点灯しっぱなし防止）。
 */
function cameraScanArea(
  kind: "fmavatar" | "clippack",
  onScanned: (secret: TransferSecret) => void,
  mode: "mailbox" | "download" = "mailbox",
  /** download モードで `recvclip:`（クリップパック受信用）QR を読んだときの処理（アバター受信ダイアログ用）。 */
  onClipRecvScanned?: (secret: TransferSecret) => void,
): { element: HTMLElement; stop: () => void } {
  const scanner = new QrScanner();
  let scanning = false;
  const video = el("video", { class: "qr-scan-video", autoplay: true, muted: true, playsinline: "true" });
  const scanStatus = el("p", { class: "muted" });
  const preview = el("div", { class: "qr-scan-preview hidden" }, video, scanStatus);

  const stopScan = (): void => {
    scanner.stop();
    scanning = false;
    preview.classList.add("hidden");
  };

  const startBtn = el("button", {
    class: "btn small",
    text: "カメラで読み取る",
    onclick: () => {
      void (async () => {
        if (scanning) {
          stopScan();
          return;
        }
        preview.classList.remove("hidden");
        scanStatus.className = "muted";
        scanStatus.textContent =
          mode === "download"
            ? "Unity 側に表示された QR コードをカメラへ写してください。"
            : `Unity 側で${KIND_LABEL[kind]}の受信待ちを開始し、QR コードをカメラへ写してください。`;
        scanning = true;
        try {
          await scanner.start(
            video,
            (text) => {
              const parsed = parseScannedQr(text);
              if (mode === "download") {
                if (parsed) {
                  // send: / sendclip: は「Unity が受け取る側」の QR。受信ダイアログでは使えない
                  scanStatus.className = "field-error";
                  scanStatus.textContent =
                    "これは Unity の「受信待ち」QR（Web から Unity へ送るためのもの）です。受信するには、Unity 側で送信して表示された QR を写してください。";
                  return;
                }
                const clipRecv = parseClipPackRecvSecret(text);
                if (clipRecv) {
                  if (kind === "clippack") {
                    stopScan();
                    onScanned(clipRecv);
                  } else if (onClipRecvScanned) {
                    stopScan();
                    onClipRecvScanned(clipRecv);
                  }
                  return;
                }
                const plain = parseTransferSecret(text);
                if (plain) {
                  stopScan();
                  onScanned(plain);
                }
                return;
              }
              if (parsed) {
                if (parsed.kind !== kind) {
                  scanStatus.className = "field-error";
                  scanStatus.textContent = `Unity 側で${KIND_LABEL[kind]}の受信待ちを開始してから、もう一度お試しください。`;
                  return;
                }
                stopScan();
                onScanned(parsed.secret);
              }
            },
            () => {
              /* デコード中の一時的なエラーは無視して継続する */
            },
          );
        } catch (e) {
          // 起動待ちの間にダイアログを閉じた・ボタンで止めた場合は何も表示しない
          if ((e as Error).name === "AbortError") return;
          scanning = false;
          preview.classList.add("hidden");
          scanStatus.className = "field-error";
          scanStatus.textContent = "カメラが使用できません。コードを手入力する方法をご利用ください。";
        }
      })();
    },
  });

  return { element: el("div", { class: "field" }, startBtn, preview), stop: stopScan };
}

function errorText(e: unknown): string {
  if (e instanceof RelayError) return e.message;
  const d = describeError(e);
  return d.detail ? `${d.title}: ${d.detail}` : d.title;
}

/** リレーの「同時に保存できる件数の上限」エラーかどうか（status だけでは他の 429 と区別がつかないため文言で判定）。 */
function isCapacityLimitError(e: unknown): boolean {
  return e instanceof RelayError && e.message.includes("同時に保存できる件数の上限");
}

/**
 * この端末から送った転送のうち、リレーに今も残っているものを返す（新しい順）。
 * リレーに問い合わせられなかった場合は、端末の控え（期限内のもの）をそのまま返し error を付ける。
 */
async function loadAliveUploads(): Promise<{ entries: SendHistoryEntry[]; error?: string }> {
  const candidates = listSendHistoryEntries().slice(0, 20);
  if (candidates.length === 0) return { entries: [] };
  try {
    applyUploadStatus(await checkUploadStatus(candidates.map((e) => ({ code: e.code, deleteToken: e.deleteToken }))));
    return { entries: listSendHistoryEntries() };
  } catch (e) {
    return { entries: candidates, error: errorText(e) };
  }
}

/**
 * 上限エラー時に表示する「送信履歴から取り消して再試行」の候補一覧。
 * 各候補の「これを取り消して再試行」ボタンを押すと revoke → 履歴削除 → retry を呼ぶ。
 */
function capacityLimitHelper(retry: () => void): HTMLElement {
  const entries = listSendHistoryEntries();
  if (entries.length === 0) {
    return el("p", { class: "muted", text: "送信履歴はありません。受信側でダウンロードするか、時間をおいて再試行してください。" });
  }
  const msg = el("p", { class: "muted" });
  const list = el(
    "div",
    { class: "field" },
    ...entries.map((entry) => {
      const row = el(
        "div",
        { class: "row" },
        el("span", { text: `${entry.fileName}（${KIND_LABEL[entry.kind]}・期限 ${formatExpiry(entry.expiresAt)}）` }),
        el("button", {
          class: "btn small",
          text: "これを取り消して再試行",
          onclick: (ev: Event) => {
            const btn = ev.currentTarget as HTMLButtonElement;
            btn.setAttribute("disabled", "");
            void (async () => {
              try {
                await revokeUpload(entry.code, entry.deleteToken);
                removeSendHistoryEntry(entry.code);
                retry();
              } catch (e) {
                btn.removeAttribute("disabled");
                msg.className = "field-error";
                msg.textContent = errorText(e);
              }
            })();
          },
        }),
      );
      return row;
    }),
  );
  return el(
    "div",
    { class: "field" },
    el("p", { class: "muted", text: "送信履歴から古い転送を取り消して再試行できます。" }),
    list,
    msg,
  );
}

function copyButton(text: string): HTMLElement {
  const btn = el("button", {
    class: "btn small",
    text: "コピー",
    onclick: () => {
      void navigator.clipboard?.writeText(text).then(
        () => {
          btn.textContent = "コピーしました";
        },
        () => {
          /* クリップボードが使えない環境では画面の文字列を手入力してもらう */
        },
      );
    },
  });
  return btn;
}

function formatExpiry(iso: string): string {
  if (!iso) return "-";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleTimeString("ja-JP");
}

/**
 * アップロード → コード/QR 表示までの一連の流れ（送信履歴への記録・取り消しボタン・
 * 上限エラー時の「送信履歴から取り消して再試行」導線を含む）。
 * 上限エラーで候補の転送を取り消したときは同じ関数を呼び直して再試行する。
 */
function runUploadFlow(opts: {
  getPlainBytes: () => ArrayBuffer;
  fileName: string;
  kind: "fmavatar" | "clippack";
  status: HTMLElement;
  result: HTMLElement;
  controller: AbortController;
}): void {
  const { getPlainBytes, fileName, kind, status, result, controller } = opts;
  const attempt = (): void => {
    void (async () => {
      try {
        clear(result);
        status.className = "muted";
        status.textContent = "暗号化しています…";
        const enc = await encryptForTransfer(getPlainBytes());
        status.textContent = "アップロードしています…";
        const up = await uploadToRelay(enc.cipherBytes, { signal: controller.signal });
        addSendHistoryEntry({
          code: up.code,
          deleteToken: up.deleteToken,
          expiresAt: up.expiresAt,
          fileName,
          kind,
          createdAt: new Date().toISOString(),
        });
        const secret = formatSecret(up.code, enc.keyB64, enc.ivB64);

        status.textContent = "Unity の FaceMaker ウィンドウで、この6桁コードと鍵の文字列を貼り付けてください。";
        const revokeMsg = el("p", { class: "muted" });
        const revokeBtn = el("button", {
          class: "btn small danger",
          text: "この転送を取り消す",
          onclick: () => {
            revokeBtn.setAttribute("disabled", "");
            void (async () => {
              try {
                await revokeUpload(up.code, up.deleteToken);
                removeSendHistoryEntry(up.code);
                revokeMsg.textContent = "取り消しました。このコードは使えません。";
              } catch (e) {
                revokeBtn.removeAttribute("disabled");
                revokeMsg.textContent = errorText(e);
              }
            })();
          },
        });

        result.append(
          el("div", { class: "transfer-code" }, up.code),
          el("p", {
            class: "field-error",
            text: "※この6桁だけではUnityに送れません。下の文字列（コード:鍵:IV）が必要です。",
          }),
          el("label", { text: "Unity に貼り付ける文字列（送信に必要な全データです）" }),
          el("p", { class: "transfer-secret", text: secret }),
          el("div", { class: "row" }, copyButton(secret), up.deleteToken ? revokeBtn : null),
          revokeMsg,
          el("p", { class: "muted", text: `有効期限: ${formatExpiry(up.expiresAt)}（1時間、受信されると即消えます）` }),
        );
      } catch (e) {
        if ((e as Error).name === "AbortError") return;
        status.textContent = errorText(e);
        status.className = "field-error";
        if (isCapacityLimitError(e)) {
          result.append(capacityLimitHelper(attempt));
        }
      }
    })();
  };
  attempt();
}

// ---------------- 送信 ----------------

/** .fmapply をリレーで送るダイアログ。 */
export function openSendDialog(options: ApplyOptions): void {
  const avatar = store.avatar;
  const project = store.project;
  if (!avatar || !project) return;

  let bytes: Uint8Array;
  try {
    bytes = serializeFmApply({
      avatarManifest: { avatarName: avatar.manifest.avatarName, avatarUid: avatar.manifest.avatarUid },
      project,
      options,
    });
  } catch (e) {
    const d = describeError(e);
    store.setBanner({ kind: "error", title: d.title, detail: d.detail, hint: d.hint });
    return;
  }

  const target = pendingSendTarget;
  const controller = new AbortController();
  const status = el("p", { class: "muted", text: "暗号化しています…" });
  const result = el("div", {});
  const cameraArea = target ? null : cameraScanArea("fmavatar", (secret) => sendToTarget(secret));
  const body = el(
    "div",
    {},
    el("p", {
      class: "muted",
      text: target
        ? "Unity が受信待ちです。この .fmapply を直接 Unity へ送ります。"
        : `${fmApplyFileName(avatar.manifest.avatarName)}（${(bytes.length / 1024).toFixed(0)} KB）をリレー経由で Unity へ送ります。`,
    }),
    cameraArea
      ? el(
          "ol",
          { class: "step-list" },
          el("li", { text: "Unity 側の「受信待ちにする」を押し、QR コードを表示させる" }),
          el("li", { text: "下の「カメラで読み取る」を押し、そのQRコードをカメラに写す（自動的に送信されます）" }),
        )
      : null,
    cameraArea ? cameraArea.element : null,
    cameraArea
      ? el("p", { class: "muted", text: "カメラが使えない場合は、下に表示されるコードを手入力することもできます。" })
      : null,
    status,
    result,
  );

  // ✕・背景クリック・「閉じる」のどれで閉じても通信中断とカメラ停止を行う
  const handle = modal(
    "リレーで送信",
    body,
    [el("button", { class: "btn ghost", text: "閉じる", onclick: () => handle.close() })],
    () => {
      controller.abort();
      cameraArea?.stop();
    },
  );

  const sendToTarget = (t: TransferSecret): void => {
    // 成功・失敗にかかわらず投函先は1回で破棄する（同じ鍵・IV での再暗号化＝GCM IV 再利用を防ぐ）
    if (pendingSendTarget === t) {
      pendingSendTarget = null;
      store.notify();
    }
    if (!consumeMailboxTarget(t)) {
      status.textContent = USED_TARGET_MESSAGE;
      status.className = "field-error";
      return;
    }
    void (async () => {
      try {
        const plain = bytes.slice().buffer as ArrayBuffer;
        const cipher = await encryptWithKey(plain, t.keyB64, t.ivB64);
        status.className = "muted";
        status.textContent = "送信しています…";
        await fillMailbox(t.code, cipher, { signal: controller.signal });
        status.textContent = "Unity へ送信しました。Unity 側の FaceMaker ウィンドウを確認してください。";
      } catch (e) {
        if ((e as Error).name === "AbortError") return;
        status.textContent = errorText(e);
        status.className = "field-error";
      }
    })();
  };

  if (target) {
    sendToTarget(target);
  } else {
    runUploadFlow({
      getPlainBytes: () => bytes.slice().buffer as ArrayBuffer,
      fileName: fmApplyFileName(avatar.manifest.avatarName),
      kind: "fmavatar",
      status,
      result,
      controller,
    });
  }
}

// ---------------- 受信 ----------------

/** コード文字列から .fmavatar を受信して読み込む。onProgress の pct はダウンロード中のみ 0-100（不明なら undefined）。 */
export async function receiveAvatar(
  secret: TransferSecret,
  onProgress?: (msg: string, pct?: number) => void,
  signal?: AbortSignal,
): Promise<void> {
  const cipher = await downloadCipher(secret, onProgress, signal);
  onProgress?.("復号しています…");
  const plain = await decryptFromTransfer(cipher, secret.keyB64, secret.ivB64);
  // キャンセル後は store を変更しない
  throwIfAborted(signal);
  onProgress?.("読み込んでいます…");
  await store.loadFromBytes(new Uint8Array(plain), true);
}

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) {
    const e = new Error("キャンセルされました。");
    e.name = "AbortError";
    throw e;
  }
}

function isAbortError(e: unknown): boolean {
  return (e as Error | null)?.name === "AbortError";
}

/** 受信共通: 暗号化済みバイト列をダウンロードする（進捗通知・キャンセル対応）。 */
async function downloadCipher(
  secret: TransferSecret,
  onProgress?: (msg: string, pct?: number) => void,
  signal?: AbortSignal,
): Promise<ArrayBuffer> {
  throwIfAborted(signal);
  onProgress?.("ダウンロードしています…", 0);
  const cipher = await downloadFromRelay(secret.code, {
    signal,
    onProgress: (loaded, total) => {
      const pct = total > 0 ? Math.round((loaded / total) * 100) : undefined;
      onProgress?.(`ダウンロードしています…${pct !== undefined ? ` ${pct}%` : ""}`, pct);
    },
  });
  throwIfAborted(signal);
  return cipher;
}

/** 6桁コード + 鍵の文字列を入力して .fmavatar を受信するダイアログ。 */
export function openReceiveDialog(initial = ""): void {
  const input = el("input", {
    class: "text-input",
    type: "text",
    value: initial,
    placeholder: "123456:鍵:IV（QR の URL を貼り付けても可）",
    autocapitalize: "off",
    autocorrect: "off",
    spellcheck: "false",
  });
  const status = el("p", { class: "muted", text: "Unity の FaceMaker ウィンドウに表示された文字列を貼り付けてください。" });
  const progress = transferProgress();
  const controller = new AbortController();
  let busy = false;

  /** クリップパック受信用（recvclip:）の文字列/QR だった場合は、クリップ受信ダイアログへ切り替える。 */
  const switchToClipReceive = (clip: TransferSecret): void => {
    handle.close();
    openClipPackReceiveDialog(formatSecret(clip.code, clip.keyB64, clip.ivB64));
  };

  const run = (): void => {
    if (busy) return;
    const clip = parseClipPackRecvSecret(input.value);
    if (clip) {
      switchToClipReceive(clip);
      return;
    }
    const secret = parseTransferSecret(input.value);
    if (!secret) {
      status.className = "field-error";
      status.textContent = "形式が正しくありません（例: 123456:<43文字の鍵>:<16文字のIV>）。";
      return;
    }
    busy = true;
    status.className = "muted";
    progress.show();
    void (async () => {
      try {
        await receiveAvatar(
          secret,
          (m, pct) => {
            status.textContent = m;
            progress.update(pct);
          },
          controller.signal,
        );
        if (controller.signal.aborted) return;
        progress.hide();
        handle.close();
        store.setTab("expression");
        store.setBanner({
          kind: "info",
          title: "アバターを受信しました",
          detail: `${store.avatar?.manifest.avatarName ?? ""} を読み込みました。`,
          hint: null,
        });
      } catch (e) {
        if (isAbortError(e) || controller.signal.aborted) return;
        busy = false;
        progress.hide();
        status.className = "field-error";
        status.textContent = errorText(e);
      }
    })();
  };

  input.addEventListener("keydown", (e) => {
    if ((e as KeyboardEvent).key === "Enter") run();
  });
  // 貼り付け（または入力）した時点で有効な形式なら、ボタンを押さず自動的に受信する
  // （コピペ一つで完結させるため）。
  input.addEventListener("input", () => {
    if (parseTransferSecret(input.value) || parseClipPackRecvSecret(input.value)) run();
  });

  // カメラで Unity の送信結果 QR を読み取った場合も、手入力と同じ受信処理（run）に合流させる。
  const cameraArea = cameraScanArea(
    "fmavatar",
    (secret) => {
      input.value = formatSecret(secret.code, secret.keyB64, secret.ivB64);
      status.className = "muted";
      run();
    },
    "download",
    (clip) => switchToClipReceive(clip),
  );

  const handle = modal(
    "コードで受信",
    el(
      "div",
      { class: "field" },
      el("label", { text: "コード:鍵:IV" }),
      input,
      cameraArea.element,
      status,
      progress.element,
    ),
    [
      el("button", { class: "btn ghost", text: "キャンセル", onclick: () => handle.close() }),
      el("button", { class: "btn primary", text: "受信する", onclick: run }),
    ],
    // ✕・背景クリック・キャンセルのどれで閉じても、カメラ停止と受信の中断を行う
    () => {
      controller.abort();
      cameraArea.stop();
    },
  );
  setTimeout(() => input.focus(), 30);
}

// ---------------- クリップパック送信 ----------------

/** 選択したクリップを .fmclippack.json としてリレーで送るダイアログ。 */
export function openClipPackSendDialog(clipIndices: number[]): void {
  const project = store.project;
  if (!project) return;
  const clips = project.clips.filter((c) => clipIndices.includes(c.index));
  if (clips.length === 0) return;

  const pack = buildClipPack(clips);
  const bytes = new TextEncoder().encode(JSON.stringify(pack));
  const baseName = clips.length === 1 ? clips[0].name : `${store.avatar?.manifest.avatarName ?? "avatar"}_表情パック`;
  const fileName = clipPackFileName(baseName);

  const target = pendingClipPackSendTarget;
  const controller = new AbortController();
  const status = el("p", { class: "muted", text: "暗号化しています…" });
  const result = el("div", {});
  const cameraArea = target ? null : cameraScanArea("clippack", (secret) => sendToTarget(secret));
  const body = el(
    "div",
    {},
    el("p", {
      class: "muted",
      text: target
        ? "Unity が受信待ちです。このクリップパックを直接 Unity へ送ります。"
        : `${fileName}（${(bytes.length / 1024).toFixed(0)} KB）をリレー経由で Unity へ送ります。`,
    }),
    cameraArea ? cameraArea.element : null,
    status,
    result,
  );

  const handle = modal(
    "クリップをリレーで送信",
    body,
    [el("button", { class: "btn ghost", text: "閉じる", onclick: () => handle.close() })],
    () => {
      controller.abort();
      cameraArea?.stop();
    },
  );

  const sendToTarget = (t: TransferSecret): void => {
    // 成功・失敗にかかわらず投函先は1回で破棄する（同じ鍵・IV での再暗号化＝GCM IV 再利用を防ぐ）
    if (pendingClipPackSendTarget === t) {
      pendingClipPackSendTarget = null;
      store.notify();
    }
    if (!consumeMailboxTarget(t)) {
      status.textContent = USED_TARGET_MESSAGE;
      status.className = "field-error";
      return;
    }
    void (async () => {
      try {
        const plain = bytes.slice().buffer as ArrayBuffer;
        const cipher = await encryptWithKey(plain, t.keyB64, t.ivB64);
        status.className = "muted";
        status.textContent = "送信しています…";
        await fillMailbox(t.code, cipher, { signal: controller.signal });
        status.textContent = "Unity へ送信しました。Unity 側の FaceMaker ウィンドウを確認してください。";
      } catch (e) {
        if ((e as Error).name === "AbortError") return;
        status.textContent = errorText(e);
        status.className = "field-error";
      }
    })();
  };

  if (target) {
    sendToTarget(target);
  } else {
    runUploadFlow({
      getPlainBytes: () => bytes.slice().buffer as ArrayBuffer,
      fileName,
      kind: "clippack",
      status,
      result,
      controller,
    });
  }
}

// ---------------- クリップパック受信 ----------------

/** コード文字列から .fmclippack.json を受信し、指定アバターへ読み込む。onProgress の pct はダウンロード中のみ 0-100（不明なら undefined）。 */
export async function receiveClipPack(
  secret: TransferSecret,
  onProgress?: (msg: string, pct?: number) => void,
  signal?: AbortSignal,
): Promise<void> {
  const cipher = await downloadCipher(secret, onProgress, signal);
  onProgress?.("復号しています…");
  const plain = await decryptFromTransfer(cipher, secret.keyB64, secret.ivB64);
  // キャンセル後は store を変更しない
  throwIfAborted(signal);
  onProgress?.("読み込んでいます…");
  const json = new TextDecoder().decode(plain);
  const { addedCount, missingShapeNames } = store.importClipPack(json);
  if (missingShapeNames.length > 0) {
    store.setBanner({
      kind: "info",
      title: `${addedCount} 件のクリップを読み込みました`,
      detail: `一致しないシェイプキー ${missingShapeNames.length} 件は無視されました: ${missingShapeNames.join(", ")}`,
      hint: null,
    });
  }
}

/** 6桁コード + 鍵の文字列を入力して .fmclippack.json を受信するダイアログ。 */
export function openClipPackReceiveDialog(initial = ""): void {
  const input = el("input", {
    class: "text-input",
    type: "text",
    value: initial,
    placeholder: "123456:鍵:IV（QR の URL を貼り付けても可）",
    autocapitalize: "off",
    autocorrect: "off",
    spellcheck: "false",
  });
  const status = el("p", { class: "muted", text: "Unity の FaceMaker ウィンドウに表示された文字列を貼り付けてください。" });
  const progress = transferProgress();

  const currentAvatar = store.avatar;
  const savedAvatars = store.savedAvatars;
  const hasTarget = !!currentAvatar || savedAvatars.length > 0;

  const select = el(
    "select",
    { class: "text-input" },
    currentAvatar ? el("option", { value: "current", text: `現在のアバター（${currentAvatar.manifest.avatarName}）` }) : null,
    ...savedAvatars
      .filter((a) => !currentAvatar || a.id !== currentAvatar.id)
      .map((a) => el("option", { value: `saved:${a.id}`, text: a.name })),
  ) as HTMLSelectElement;

  const controller = new AbortController();
  let busy = false;

  /** `recvclip:` 形式（URL 含む）と手入力の `code:鍵:IV` の両方を受け付ける。 */
  const parseInput = (v: string): TransferSecret | null => parseClipPackRecvSecret(v) ?? parseTransferSecret(v);

  const run = (): void => {
    if (busy) return;
    if (!hasTarget) return;
    const secret = parseInput(input.value);
    if (!secret) {
      status.className = "field-error";
      status.textContent = "形式が正しくありません（例: 123456:<43文字の鍵>:<16文字のIV>）。";
      return;
    }
    busy = true;
    status.className = "muted";
    progress.show();
    void (async () => {
      try {
        const choice = select.value;
        if (choice.startsWith("saved:")) {
          status.textContent = "アバターを切り替えています…";
          await store.loadSaved(choice.slice("saved:".length));
        }
        await receiveClipPack(
          secret,
          (m, pct) => {
            status.textContent = m;
            progress.update(pct);
          },
          controller.signal,
        );
        if (controller.signal.aborted) return;
        progress.hide();
        handle.close();
        store.setTab("expression");
        store.setBanner({ kind: "info", title: "クリップを受信しました", detail: "", hint: null });
      } catch (e) {
        if (isAbortError(e) || controller.signal.aborted) return;
        busy = false;
        progress.hide();
        status.className = "field-error";
        status.textContent = errorText(e);
      }
    })();
  };

  input.addEventListener("keydown", (e) => {
    if ((e as KeyboardEvent).key === "Enter") run();
  });
  // 貼り付け（または入力）した時点で有効な形式なら自動的に受信する（アバター受信と同じ挙動）
  input.addEventListener("input", () => {
    if (parseInput(input.value)) run();
  });

  // カメラで Unity の送信結果 QR（recvclip: / code:鍵:IV）を読み取った場合も run に合流させる
  const cameraArea = hasTarget
    ? cameraScanArea(
        "clippack",
        (secret) => {
          input.value = formatSecret(secret.code, secret.keyB64, secret.ivB64);
          status.className = "muted";
          run();
        },
        "download",
      )
    : null;

  const body = hasTarget
    ? el(
        "div",
        { class: "field" },
        el("label", { text: "コード:鍵:IV" }),
        input,
        cameraArea ? cameraArea.element : null,
        el("label", { text: "読み込み先のアバター" }),
        select,
        status,
        progress.element,
      )
    : el(
        "div",
        { class: "field" },
        el("p", { class: "field-error", text: "先に .fmavatar を読み込んでください。" }),
      );

  const handle = modal(
    "コードでクリップを受信",
    body,
    hasTarget
      ? [
          el("button", { class: "btn ghost", text: "キャンセル", onclick: () => handle.close() }),
          el("button", { class: "btn primary", text: "受信する", onclick: run }),
        ]
      : [el("button", { class: "btn ghost", text: "閉じる", onclick: () => handle.close() })],
    // ✕・背景クリック・キャンセルのどれで閉じても、カメラ停止と受信の中断を行う
    () => {
      controller.abort();
      cameraArea?.stop();
    },
  );
  if (hasTarget) setTimeout(() => input.focus(), 30);
}

// ---------------- 設定 ----------------

/** 送信履歴の1行（ファイル名・種別・日時・有効期限 + 取り消しボタン）。 */
function sendHistoryRow(entry: SendHistoryEntry, onRemoved: () => void): HTMLElement {
  const msg = el("p", { class: "muted" });
  const removeBtn = el("button", {
    class: "btn small danger",
    text: "取り消す",
    onclick: () => {
      removeBtn.setAttribute("disabled", "");
      void (async () => {
        try {
          await revokeUpload(entry.code, entry.deleteToken);
          removeSendHistoryEntry(entry.code);
          onRemoved();
        } catch (e) {
          removeBtn.removeAttribute("disabled");
          msg.className = "field-error";
          msg.textContent = errorText(e);
        }
      })();
    },
  });
  return el(
    "div",
    { class: "field" },
    el("div", {
      class: "row",
      text: `${entry.fileName}（${KIND_LABEL[entry.kind]}） 送信: ${formatExpiry(entry.createdAt)} / 期限: ${formatExpiry(entry.expiresAt)}`,
    }),
    el("div", { class: "row" }, removeBtn),
    msg,
  );
}

// 画面の再描画（store.notify）で <details> が閉じたりメッセージが消えたりしないよう、
// 開閉状態・保存メッセージ・直近の問い合わせ結果をモジュール内に保持する。
let relaySettingsOpen = false;
let relaySaveMessage = "";
let sendHistoryOpen = false;
let sendHistoryCache: { entries: SendHistoryEntry[]; error?: string } | null = null;

/** リレー上の自分の転送（<details>、初期は閉じておく）。開くたびにリレーへ存在確認して描画する。 */
function sendHistorySection(): HTMLElement {
  const list = el("div", {});
  let loading = false;
  const show = (r: { entries: SendHistoryEntry[]; error?: string }): void => {
    clear(list);
    if (r.error) list.append(el("p", { class: "field-error", text: `${r.error}（この端末の控えを表示しています）` }));
    if (r.entries.length === 0) {
      list.append(el("p", { class: "muted", text: "この端末から送った転送で、リレーに残っているものはありません。" }));
      return;
    }
    for (const entry of r.entries) list.append(sendHistoryRow(entry, render));
  };
  const render = (): void => {
    if (loading) return;
    loading = true;
    clear(list);
    list.append(el("p", { class: "muted", text: "リレーに問い合わせています…" }));
    void (async () => {
      const r = await loadAliveUploads();
      loading = false;
      sendHistoryCache = r;
      show(r);
    })();
  };
  const details = el(
    "details",
    { class: "advanced" },
    el("summary", { text: "リレー上の自分の転送" }),
    el(
      "div",
      { class: "field" },
      el("p", {
        class: "muted",
        text: "この端末から送った転送のうち、まだ受信されずリレーに残っているものだけを表示します。画面を閉じて取り消せなくなった転送も、ここから取り消せます。",
      }),
      el("button", { class: "btn small ghost", text: "再読み込み", onclick: render }),
      list,
    ),
  );
  const d = details as HTMLDetailsElement;
  // 再描画前に開いていた場合は開いた状態で復元する（問い合わせ済みなら結果を再利用し、リレーへ再問い合わせしない）
  let skipRestoreToggle = false;
  if (sendHistoryOpen) {
    skipRestoreToggle = true;
    d.open = true;
    if (sendHistoryCache) show(sendHistoryCache);
    else render();
  }
  details.addEventListener("toggle", () => {
    sendHistoryOpen = d.open;
    if (skipRestoreToggle) {
      // 上の d.open = true による toggle イベントは無視する
      skipRestoreToggle = false;
      if (d.open) return;
    }
    if (d.open) render();
  });
  return details;
}

/** リレー URL の設定 UI（未デプロイのため既定は未設定）。 */
export function relaySettings(): HTMLElement {
  const input = el("input", {
    class: "text-input",
    type: "url",
    value: getRelayBaseUrl(),
    placeholder: "https://facemaker-relay.<subdomain>.workers.dev",
  });
  const msg = el("p", { class: "muted", text: relaySaveMessage });
  const details = el(
      "details",
      { class: "advanced" },
      el("summary", { text: "リレー設定" }),
      el(
        "div",
        { class: "field" },
        el("label", { text: "リレー URL" }),
        input,
        el(
          "div",
          { class: "row" },
          el("button", {
            class: "btn small",
            text: "保存",
            onclick: () => {
              setRelayBaseUrl(input.value);
              relaySaveMessage = getRelayBaseUrl() ? `保存しました: ${getRelayBaseUrl()}` : "未設定に戻しました。";
              msg.textContent = relaySaveMessage;
              store.notify();
            },
          }),
        ),
        msg,
        el("p", {
          class: "muted",
          text: "Cloudflare Worker (facemaker-relay) の URL。ローカル検証時は http://127.0.0.1:8787 を指定します。",
        }),
      ),
    ) as HTMLDetailsElement;
  details.open = relaySettingsOpen;
  details.addEventListener("toggle", () => {
    relaySettingsOpen = details.open;
    // 閉じたら保存メッセージは役目を終えたので消す
    if (!details.open) relaySaveMessage = "";
  });
  return el("div", {}, details, sendHistorySection());
}
